import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Network as NetIcon, Copy, Coins, Award, Crown, Lock, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { logError } from "../lib/logger";
import { useAuth } from "../context/AuthContext";

const VIP_NP_COST = 30;

const WD_STATUS = {
  approved: { label: "Approvato", cls: "bg-emerald-500/15 text-emerald-400" },
  rejected: { label: "Rifiutato", cls: "bg-red-500/15 text-red-400" },
  pending: { label: "In attesa", cls: "bg-amber-500/15 text-amber-400" },
};
const wdStatus = (s) => WD_STATUS[s] || WD_STATUS.pending;

export default function Network() {
  const { user, checkAuth } = useAuth();
  const [me, setMe] = useState(null);
  const [tree, setTree] = useState({ l1: [], l2: [], l3: [] });
  const [wds, setWds] = useState([]);
  const [type, setType] = useState("NP");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [m, t, w] = await Promise.all([
        api.get("/network/me"), api.get("/network/tree"), api.get("/network/withdrawals"),
      ]);
      setMe(m.data); setTree(t.data); setWds(w.data);
    } catch (e) { logError("network load", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const withdraw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/network/withdraw", { type, amount_eur: parseFloat(amount) });
      toast.success("Richiesta di prelievo inviata");
      setAmount(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const buyVip = async () => {
    if (!window.confirm(`Acquistare 30 giorni di VIP con ${VIP_NP_COST} NP?`)) return;
    try {
      const { data } = await api.post("/network/buy-vip-with-np");
      toast.success(`VIP attivo fino al ${new Date(data.premium_until).toLocaleDateString("it-IT")}`);
      load(); checkAuth && checkAuth();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const refLink = me?.ref_code ? `${window.location.origin}/auth?ref=${me.ref_code}` : "";
  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Link copiato!"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento...</div>;

  if (!me?.network_active) {
    return (
      <div className="max-w-md mx-auto px-6 py-16" data-testid="network-locked">
        <div className="bg-[#141414] border border-blue-500/30 rounded-md p-8 relative overflow-hidden text-center">
          <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 blur-3xl rounded-full" />
          <div className="relative">
            <Lock className="mx-auto text-amber-400 mb-3" size={28} />
            <h1 className="text-2xl font-bold mb-2">Network non attivo</h1>
            <p className="text-sm text-zinc-400">
              Il Raeinex Network e il Wallet si sbloccano automaticamente diventando <span className="text-amber-400 font-medium">VIP</span> o <span className="text-blue-400 font-medium">INVESTOR</span>.
              Guadagna commissioni NP, bonus NB e ottieni il tuo link di invito personale.
            </p>
            <Link to="/pricing" data-testid="network-upgrade-btn"
              className="inline-flex items-center justify-center gap-2 mt-6 px-5 py-3 rounded-md bg-blue-500 text-white font-semibold hover:bg-blue-400 transition-colors">
              <Crown size={16} /> Scopri i piani
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canBuyVip = me.np_balance >= VIP_NP_COST;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="network-page">
      <div className="flex items-center gap-2 mb-1">
        <NetIcon className="text-blue-500" size={26} />
        <h1 className="text-3xl font-bold tracking-tight">Network</h1>
      </div>
      <p className="text-sm text-zinc-400 mb-8">Raeinex Network · invita e guadagna NP</p>

      {/* Plan expiry */}
      {user?.role === "admin" ? (
        <div data-testid="plan-expiry" className="mb-6 flex items-center gap-2 text-sm bg-emerald-500/10 border border-emerald-500/30 rounded-md px-4 py-3 text-emerald-400">
          <CalendarClock size={16} /> Account admin · accesso completo permanente
        </div>
      ) : me.subscription_plan ? (
        <div data-testid="plan-expiry" className={`mb-6 flex items-center gap-2 text-sm rounded-md px-4 py-3 border ${(me.days_remaining ?? 0) > 0 ? "bg-blue-500/10 border-blue-500/30 text-blue-300" : "bg-red-500/10 border-red-500/30 text-red-400"}`}>
          <CalendarClock size={16} />
          {(me.days_remaining ?? 0) > 0
            ? `Il tuo piano ${me.subscription_plan === "investor_yearly" ? "INVESTOR" : "VIP"} scade tra ${me.days_remaining} ${me.days_remaining === 1 ? "giorno" : "giorni"}${me.premium_until ? ` (${new Date(me.premium_until).toLocaleDateString("it-IT")})` : ""}.`
            : `Il tuo piano ${me.subscription_plan === "investor_yearly" ? "INVESTOR" : "VIP"} è scaduto. Rinnova per continuare.`}
        </div>
      ) : null}

      {/* Invite link */}
      <div className="bg-[#141414] border border-white/10 rounded-md p-5 mb-6">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Il tuo link di invito</div>
        <div className="flex flex-wrap items-center gap-3">
          <code data-testid="ref-link" className="flex-1 min-w-[220px] text-sm text-blue-400 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 truncate">{refLink}</code>
          <button data-testid="copy-ref-btn" onClick={copy}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors">
            <Copy size={15} /> Copia
          </button>
        </div>
        <div className="text-xs text-zinc-500 mt-2">Codice: <span className="font-mono text-zinc-300">{me.ref_code}</span></div>
      </div>

      {/* Wallets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div data-testid="np-wallet" className="bg-gradient-to-br from-blue-600/20 to-[#141414] border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 text-blue-400 mb-2"><Coins size={18} /> <span className="text-xs uppercase tracking-wider">Portafoglio NP</span></div>
          <div className="text-3xl font-bold">{me.np_balance} <span className="text-lg text-zinc-400">NP</span></div>
          <div className="text-sm text-zinc-400 mt-1">≈ {me.np_value_eur} €</div>
        </div>
        <div data-testid="nb-wallet" className="bg-gradient-to-br from-amber-500/20 to-[#141414] border border-amber-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 text-amber-400 mb-2"><Award size={18} /> <span className="text-xs uppercase tracking-wider">Portafoglio NB</span></div>
          <div className="text-3xl font-bold">{me.nb_balance} <span className="text-lg text-zinc-400">NB</span></div>
          <div className="text-sm text-zinc-400 mt-1">≈ {me.nb_value_eur} €</div>
        </div>
      </div>

      {/* Buy VIP with NP */}
      <div className="bg-[#141414] border border-white/10 rounded-md p-5 mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Crown className="text-yellow-400" size={22} />
          <div>
            <div className="font-semibold">Acquista il pack Segnali VIP con gli NP</div>
            <div className="text-xs text-zinc-500">{VIP_NP_COST} NP = 30 giorni di segnali VIP</div>
          </div>
        </div>
        <button data-testid="buy-vip-np-btn" onClick={buyVip} disabled={!canBuyVip}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${canBuyVip ? "bg-yellow-500 text-black hover:bg-yellow-400" : "bg-white/5 text-zinc-600 cursor-not-allowed"}`}>
          {canBuyVip ? "Acquista VIP (30 NP)" : `Servono ${VIP_NP_COST} NP`}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Withdraw + history */}
        <div className="bg-[#141414] border border-white/10 rounded-md p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><Coins size={18} className="text-emerald-400" /> Preleva fondi</h3>
          <form onSubmit={withdraw} className="space-y-3">
            <div className="flex gap-2">
              {["NP", "NB"].map((t) => (
                <button key={t} type="button" data-testid={`wd-type-${t}`} onClick={() => setType(t)}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${type === t ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`}>
                  {t === "NP" ? "NP (1€)" : "NB (2,5€)"}
                </button>
              ))}
            </div>
            <input data-testid="wd-amount" type="number" step="0.01" min="0" required value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="Importo in €"
              className="w-full px-4 py-2.5 rounded-md bg-[#0A0A0A] border border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none" />
            <p className="text-xs text-zinc-500">{type === "NB" ? "I NB si prelevano a blocchi di 100€ (40 NB)." : "Prelievo NP: 1 NP = 1€."}</p>
            <button data-testid="wd-submit" type="submit"
              className="w-full px-4 py-2.5 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors">Invia richiesta</button>
          </form>

          <div className="mt-5">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Cronologia prelievi</div>
            <div className="space-y-2 max-h-56 overflow-auto" data-testid="wd-history">
              {wds.length === 0 && <div className="text-sm text-zinc-600">Nessuna richiesta.</div>}
              {wds.map((w) => (
                <div key={w.id} data-testid={`wd-row-${w.id}`} className="flex items-center justify-between text-sm bg-[#0A0A0A] rounded-md px-3 py-2">
                  <span>{w.type} · {w.amount_eur}€ <span className="text-xs text-zinc-600">· {new Date(w.created_at).toLocaleDateString("it-IT")}</span></span>
                  <span className={`text-xs px-2 py-0.5 rounded-sm ${wdStatus(w.status).cls}`}>
                    {wdStatus(w.status).label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Multilevel scheme */}
        <div className="bg-[#141414] border border-white/10 rounded-md p-6" data-testid="multilevel-scheme">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><NetIcon size={18} className="text-blue-400" /> Schema</h3>
          <div className="flex flex-col items-center mb-5">
            <div className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium">TU</div>
            <div className="w-px h-4 bg-white/20" />
            <div className="grid grid-cols-3 gap-3 w-full text-center">
              {[["L1 · 18%", me.counts.l1, "text-emerald-400"], ["L2 · 12%", me.counts.l2, "text-blue-400"], ["L3 · 5%", me.counts.l3, "text-purple-400"]].map(([lbl, n, col]) => (
                <div key={lbl} className="bg-[#0A0A0A] border border-white/10 rounded-md p-3">
                  <div className={`text-xs ${col}`}>{lbl}</div>
                  <div className="text-xl font-bold">{n}</div>
                  <div className="text-[10px] text-zinc-600">membri</div>
                </div>
              ))}
            </div>
          </div>
          {[["Livello 1 · 18%", tree.l1], ["Livello 2 · 12%", tree.l2], ["Livello 3 · 5%", tree.l3]].map(([label, arr]) => (
            <div key={label} className="mb-3">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5">{label} ({arr.length})</div>
              <div className="space-y-1 max-h-28 overflow-auto">
                {arr.length === 0 && <div className="text-sm text-zinc-600">Nessun membro.</div>}
                {arr.map((m2, i) => (
                  <div key={`${label}-${m2.email || m2.name || i}`} className="flex items-center justify-between text-sm bg-[#0A0A0A] rounded-md px-3 py-1.5">
                    <span className="truncate">{m2.name || m2.email}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm ${m2.active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-zinc-500"}`}>{m2.active ? "Attivo" : "In attesa"}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
